import { alpha, useTheme } from "@mui/material/styles";

/**
 * The application's three surface levels, in one place.
 *
 *   LEVEL 1  page      — the app background, tinted rather than flat
 *   LEVEL 2  card      — the panels that sit on it
 *   LEVEL 3  inner     — legend strips, empty states and mini-stat areas *inside* a card
 *
 * Everything is derived from the live MUI theme, which this installation lets
 * the client reconfigure from Setting → Theme Color (primary, card background,
 * drawer colour and so on are read from localStorage at theme-build time). So
 * nothing here hard-codes a brand colour — the depth comes from how the
 * configured colours are layered, which keeps that feature working.
 */
export const useSurfaces = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const ink = theme.palette.common.black;
  const accent = theme.palette.primary.main;

  /* Two-layer shadows: a tight contact shadow plus a wide soft one. A single
     large blur is what makes a card read as a Material default. */
  const shadowResting = isDark
    ? `0 1px 2px ${alpha(ink, 0.4)}, 0 8px 24px -12px ${alpha(ink, 0.6)}`
    : `0 1px 2px ${alpha(ink, 0.05)}, 0 12px 28px -18px ${alpha("#0f2744", 0.35)}`;

  const shadowLifted = isDark
    ? `0 2px 4px ${alpha(ink, 0.45)}, 0 18px 40px -16px ${alpha(ink, 0.7)}`
    : `0 2px 6px ${alpha(ink, 0.06)}, 0 22px 44px -20px ${alpha("#0f2744", 0.45)}`;

  const hairline = alpha(theme.palette.divider, isDark ? 0.55 : 0.75);

  return {
    isDark,
    accent,
    hairline,

    /**
     * Body text that has to carry weight — a client name, a row label.
     * In dark mode the configured text.primary (#7e7e7e by default) sits at
     * roughly 3:1 on the card surface, so the heading colour is used instead;
     * light mode is unchanged.
     */
    textStrong: isDark ? theme.palette.heading.headingColor : theme.palette.text.primary,
    shadowResting,
    shadowLifted,

    /** LEVEL 2 — the standard panel shell. */
    card: {
      backgroundColor: theme.palette.cardAdd.bgColor,
      border: `1px solid ${hairline}`,
      borderRadius: "18px",
      boxShadow: shadowResting,
    },

    /** LEVEL 3 — a quiet tinted area inside a card. */
    inner: {
      backgroundColor: isDark ? alpha(theme.palette.common.white, 0.035) : alpha(accent, 0.035),
      border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.4 : 0.55)}`,
      borderRadius: "14px",
    },

    /** Divider between a card's header and its body. */
    separator: `1px solid ${alpha(theme.palette.divider, isDark ? 0.4 : 0.6)}`,

    /** Entry animation, honoured only when the visitor allows motion. */
    entrance: (delay: number) => ({
      opacity: 0,
      animation: "gtSurfaceIn 380ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
      animationDelay: `${delay}ms`,
      "@keyframes gtSurfaceIn": {
        from: { opacity: 0, transform: "translateY(10px)" },
        to: { opacity: 1, transform: "none" },
      },
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        opacity: 1,
        transform: "none",
      },
    }),
  };
};

export type Surfaces = ReturnType<typeof useSurfaces>;
